import React, { ReactNode } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { StylesProvider } from '@material-ui/core/styles'
import { CssBaseline } from '@geist-ui/react'
import { QueryClient, QueryClientProvider } from 'react-query'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web'
import Header from '../header/Header'
import Footer from '../footer/Footer'
import dynamic from 'next/dynamic'
import { ApolloProvider } from '@apollo/client'
import { indexerClient } from '../../graphql/graphql-queries'
import { GeistProvider, Themes } from '@geist-ui/react'
import SecondaryDialog from '../dialog/SecondaryMarket/SecondaryDialog'

const DeleteCommentDialog = dynamic(() => import('../dialog/DeleteCommentDialog'), { ssr: false })
const NotificationDrawer = dynamic(() => import('../drawer/NotificationDrawer'), { ssr: false })
const MobileConnectDialog = dynamic(() => import('../dialog/MobileConnectDialog'), { ssr: false })
const SearchDrawer = dynamic(() => import('../drawer/SearchDrawer'), { ssr: false })
const PurchaseDialog = dynamic(() => import('../dialog/PurchaseDialog'), { ssr: false })
const CreatorCheckDialog = dynamic(() => import('../dialog/CreatorCheckDialog'), { ssr: false })
const SignInDialog = dynamic(() => import('../dialog/SignInDialog'), { ssr: false })
const VerificationDialog = dynamic(() => import('../dialog/VerificationDialog'), { ssr: false })
const CrossmintBuyDialog = dynamic(() => import('../dialog/CrossmintBuyDialog'), { ssr: false })
const CrossmintTransferDialog = dynamic(() => import('../dialog/CrossmintTransferDialog'), {
  ssr: false,
})
const CheckoutDialog = dynamic(() => import('../dialog/CheckoutDialog'), { ssr: false })

const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    background: '#101010';
    font-family: "Inter", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu;

    ul li:before {
      display: none;
    }
  }
`

const GlassTheme = Themes.createFromDark({
  type: 'glassTheme',
  palette: {
    background: '#101010',
    selection: '#d5d4fb5f',
  },
})

const Main = styled.div`
  background-color: #101010;
  margin-top: 100px;
  margin-bottom: 75px;
  min-height: calc(100vh - 150px);

  @media (min-width: 43em) {
    margin-top: 100px;
    margin-bottom: 75px;
    min-height: calc(100vh - 150px);
  }
`

type Props = {
  children?: ReactNode
}

const queryClient = new QueryClient()

const Content = ({ children }: Props): JSX.Element => {
  const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
    server: {
      apiKey:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? 'UDpd6w2w1702mYdIoKDf3bC7yTVf9Nvu'
          : 'QTbcQHixzv56H9w0fnhhPTJDPvXTnhZf',
      nodes: [
        {
          host:
            process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
              ? 'uag62y3fojptew8mp-1.a1.typesense.net'
              : 'e25l3fgs68vbmko4p-1.a1.typesense.net',
          port: 443,
          protocol: 'https',
        },
      ],
      cacheSearchResultsForSeconds: 0, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
    },
    // The following parameters are directly passed to Typesense's search API endpoint.
    //  So you can pass any parameters supported by the search endpoint below.
    //  query_by is required.
    additionalSearchParameters: {
      query_by: 'name',
    },
  })
  const searchClient = typesenseInstantsearchAdapter.searchClient

  return (
    <div>
      <ApolloProvider client={indexerClient}>
        <QueryClientProvider client={queryClient}>
          <InstantSearch indexName="videos" searchClient={searchClient}>
            <GeistProvider themes={[GlassTheme]} themeType="glassTheme">
              <Configure hitsPerPage={5} />
              <CssBaseline />
              <StylesProvider>
                <GlobalStyle />
                <Header />
                <Main>{children}</Main>
                <Footer />
                <SignInDialog />
                <VerificationDialog />
                <SecondaryDialog />
                <DeleteCommentDialog />
                <NotificationDrawer />
                <SearchDrawer />
                <MobileConnectDialog />
                <PurchaseDialog />
                <CreatorCheckDialog />
                <CrossmintBuyDialog />
                <CrossmintTransferDialog />
                <CheckoutDialog />
              </StylesProvider>
            </GeistProvider>
          </InstantSearch>
        </QueryClientProvider>
      </ApolloProvider>
    </div>
  )
}

export default Content
